import axios from 'axios'
import {SURFACES, buildPayload, buildQueryString} from '@/components/filters/capabilities'

const state = {
  currentGroup: null,
  groups: [],
  qcGroups: [],
  groupRetailers: []
}

const getters = {
  getCurrentGroup: (state) => state.currentGroup,
  getGroups: (state) => state.groups,
  getQCGroups: (state) => state.qcGroups,
  getGroupRetailers: (state) => state.groupRetailers
}

const mutations = {
  setCurrentGroup(state, group) {
    state.currentGroup = group
  },
  setGroups(state, groups) {
    state.groups = groups
  },
  setQCGroups(state, groups) {
    state.qcGroups = groups
  },
  updateGroup(state, updatedGroup) {
    const index = state.groups.findIndex(g => g.id === updatedGroup.id)
    if (index !== -1) {
      state.groups.splice(index, 1, updatedGroup)
    }
    if (state.currentGroup && state.currentGroup.id === updatedGroup.id) {
      state.currentGroup = updatedGroup
    }
  },
  updateQCGroup(state, updatedGroup) {
    const index = state.qcGroups.findIndex(g => g.id === updatedGroup.id)
    if (index !== -1) {
      state.qcGroups.splice(index, 1, updatedGroup)
    }
  },
  setGroupRetailers(state, retailers) {
    state.groupRetailers = retailers
  }
}

const actions = {
  // Fetch groups for tagging (checkout).
  //
  // Accepts either the legacy `{retailer, question_id}` shape or a full
  // PhotoFilter payload (`fromDt`/`thruDt`/`projects`/`jobs`/`department`/
  // `country`/`respIdSearchFreeForm`/`retailer`). The backend `apply_group_filters`
  // reads the shared query-param names (`fromDateVal`/`toDateVal`/etc.).
  fetchGroups(context, payload = {}) {
    // IT-707 P2.1: query construction moved to the shared capability builder,
    // which `@/store/groupFilterParams` (IT-744, box-tagging queue) now delegates
    // to as well. Still a GET, and still omits falsy rather than sending null.
    const query = buildQueryString(payload)
    const url = `/api/photo-groups/get_groups/${query ? `?${query}` : ''}`

    return axios.get(url)
      .then(response => {
        context.commit('setGroups', response.data)
        if (response.data.length > 0) {
          context.commit('setCurrentGroup', response.data[0])
        } else {
          context.commit('setCurrentGroup', null)
        }
      })
      .catch(e => { console.log(e) })
  },

  // Fetch cached list of retailers for groups
  fetchGroupRetailers(context) {
    return axios.get('/api/group-retailers/')
      .then(response => {
        context.commit('setGroupRetailers', response.data)
      })
      .catch(e => { console.log(e) })
  },

  // Tag a group
  //
  // IT-699: this used to `.catch(e => console.log(e))`, which resolved
  // `undefined` on failure. That made applyTags' try/catch dead code — it
  // never saw a rejection because nothing below it ever rejected — so a
  // failed save was announced as "Tags Applied Successfully" and the group
  // was submitted and released with nothing on it. Save paths must report
  // failure to their caller.
  tagGroup(context, payload) {
    return axios.post('/api/photo-groups/tag_group/', payload)
      .then(response => {
        context.commit('updateGroup', response.data)
        return response.data
      })
      .catch(e => {
        console.log(e)
        throw e
      })
  },

  // Submit and release assigned groups
  // IT-699: same swallow as tagGroup above. A failed submit_assigned left the
  // group checked out while every caller reported success.
  submitAssignedGroups(context) {
    return axios.post('/api/photo-groups/submit_assigned/')
      .then(() => {
        context.commit('setGroups', [])
        context.commit('setCurrentGroup', null)
      })
      .catch(e => {
        console.log(e)
        throw e
      })
  },

  // Clear assigned groups
  clearAssignedGroups(context) {
    return axios.post('/api/photo-groups/clear_assigned/')
      .then(() => {
        context.commit('setGroups', [])
        context.commit('setCurrentGroup', null)
      })
      .catch(e => { console.log(e) })
  },

  // Fetch groups for QC.
  //
  // Accepts either the legacy `{onlyNeedsReview}` shape or a full PhotoFilter
  // payload (`fromDt`/`thruDt`/`projects`/`jobs`/`department`/`retailer`/
  // `country`/`respIdSearchFreeForm`/`rgidSearchFreeForm`) plus QC-specific fields
  // (`onlyNeedsReview`, `showQCed`, `taggers`, `tags`). Backend
  // `quality_control` uses the same `apply_group_filters` helper as
  // `get_groups`, so the shape matches fetchGroups.
  fetchQCGroups(context, payload = {}) {
    // IT-707 P2.1: body construction moved to the shared capability builder,
    // including the IT-703 `groupId` revert guard.
    const body = buildPayload(SURFACES.GROUP_QC, payload)

    return axios.post('/api/photo-groups/quality_control/', body)
      .then(response => {
        context.commit('setQCGroups', response.data)
      })
      .catch(e => { console.log(e) })
  },

  // Update tags on a group during QC
  fetchGroupForPhoto(context, payload) {
    // IT-703: /query-photos is photo-centric, so Edit Tags needs to find the
    // group that owns the photo before it can edit the group's tags. Rejects
    // rather than swallowing — the caller must not fall through to a save with
    // no group, which would post an empty tag set over real work.
    const params = new URLSearchParams({
      response_group_id: payload.response_group_id,
      question_id: payload.question_id,
    })
    return axios.get(`/api/photo-groups/for_photo/?${params.toString()}`)
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        throw e
      })
  },

  bulkUpdateGroup(context, payload) {
    // IT-703: rethrow rather than swallow. Callers decide what a failed write
    // means; resolving `undefined` here let the Edit Tags dialog close as if
    // the save had worked — the same silent-success failure IT-699 fixed on
    // the tagger path and IT-701 tracks for the rest of this module.
    return axios.post('/api/photo-groups/bulk_update/', payload)
      .then(response => {
        context.commit('updateQCGroup', response.data)
        return response.data
      })
      .catch(e => {
        console.log(e)
        throw e
      })
  },

  // Submit QC reviewed groups
  submitQCGroups(context) {
    return axios.post('/api/photo-groups/submit_qc_assigned/')
      .then(() => {
        context.commit('setQCGroups', [])
      })
      .catch(e => { console.log(e) })
  },

  // Submit count entries for a group
  submitGroupCounts(context, payload) {
    return axios.post('/api/photo-groups/submit_group_counts/', payload)
      .then(response => {
        if (payload.is_qc) {
          context.commit('updateQCGroup', response.data)
        } else {
          context.commit('updateGroup', response.data)
          context.commit('setCurrentGroup', response.data)
        }
        return response.data
      })
      .catch(e => { console.log(e) })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
